// frontend/src/pages/PilotDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    ArrowLeftIcon, 
    PlusIcon, 
    CheckCircleIcon,
    ChartBarIcon,
    ChatBubbleLeftIcon,
    ClockIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';

const PilotDetail = () => {
    const { pilotId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [pilot, setPilot] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showCheckInModal, setShowCheckInModal] = useState(false);
    const [showMetricModal, setShowMetricModal] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    const [milestoneForm, setMilestoneForm] = useState({ name: '', description: '', due_date: '' });
    const [taskForm, setTaskForm] = useState({ name: '', description: '', priority: 'medium', due_date: '' });
    const [checkInForm, setCheckInForm] = useState({ date: new Date().toISOString().split('T')[0], status: 'on_track', progress_summary: '', challenges: '', next_steps: '' });
    const [metricForm, setMetricForm] = useState({ metric_category: 'efficiency', metric_name: '', baseline_value: '', target_value: '', current_value: '', unit: '%' });
    const [feedbackForm, setFeedbackForm] = useState({ provider_type: 'intern', feedback_type: 'general', rating: 5, comment: '' });

    useEffect(() => {
        loadPilotDetails();
    }, [pilotId]);

    const loadPilotDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/pilots/${pilotId}`);
            setPilot(response.data.data);
        } catch (error) {
            toast.error('Failed to load pilot details');
            navigate('/pilots');
        } finally {
            setLoading(false);
        }
    };

    const handleAddMilestone = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/pilots/${pilotId}/milestone`, milestoneForm);
            toast.success('Milestone added');
            setShowMilestoneModal(false);
            setMilestoneForm({ name: '', description: '', due_date: '' });
            loadPilotDetails();
        } catch (error) {
            toast.error('Failed to add milestone');
        }
    };

    const handleUpdateMilestone = async (milestoneId, status) => {
        try {
            await axios.put(`/pilots/milestone/${milestoneId}`, { status });
            toast.success('Milestone status updated');
            loadPilotDetails();
        } catch (error) {
            toast.error('Failed to update milestone');
        }
    };

    const handleAddTask = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/pilots/${pilotId}/task`, taskForm);
            toast.success('Task added');
            setShowTaskModal(false);
            setTaskForm({ name: '', description: '', priority: 'medium', due_date: '' });
            loadPilotDetails();
        } catch (error) {
            toast.error('Failed to add task');
        }
    };

    const handleAddCheckIn = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/pilots/${pilotId}/check-in`, checkInForm);
            toast.success('Check-in recorded');
            setShowCheckInModal(false);
            setCheckInForm({ date: new Date().toISOString().split('T')[0], status: 'on_track', progress_summary: '', challenges: '', next_steps: '' });
            loadPilotDetails();
        } catch (error) {
            toast.error('Failed to add check-in');
        }
    };

    const handleAddMetric = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/pilots/${pilotId}/metric`, metricForm);
            toast.success('Impact metric added');
            setShowMetricModal(false);
            setMetricForm({ metric_category: 'efficiency', metric_name: '', baseline_value: '', target_value: '', current_value: '', unit: '%' });
            loadPilotDetails();
        } catch (error) {
            toast.error('Failed to add impact metric');
        }
    };

    const handleAddFeedback = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`/pilots/${pilotId}/feedback`, feedbackForm);
            toast.success('Feedback recorded');
            setShowFeedbackModal(false);
            setFeedbackForm({ provider_type: 'intern', feedback_type: 'general', rating: 5, comment: '' });
            loadPilotDetails();
        } catch (error) {
            toast.error('Failed to add feedback');
        }
    };

    const handleCompletePilot = async () => {
        if (!window.confirm('Mark this pilot as completed?')) return;
        try {
            await axios.post(`/pilots/${pilotId}/complete`, { success_criteria_met: true });
            toast.success('Pilot completed successfully! 🎉');
            loadPilotDetails();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to complete pilot');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading pilot details...</div>
            </div>
        );
    }

    if (!pilot) return null;

    return (
        <div className="w-full space-y-6">
            <Link to="/pilots" className="inline-flex items-center text-sm text-gray-600 hover:text-black space-x-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to Pilots</span>
            </Link>

            {/* Top Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <div className="flex justify-between items-start flex-wrap gap-4">
                    <div>
                        <div className="flex items-center space-x-3">
                            <h1 className="text-2xl font-bold text-gray-900">{pilot.name}</h1>
                            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 capitalize">
                                {pilot.status}
                            </span>
                        </div>
                        <p className="text-gray-600 mt-1 text-sm">{pilot.description}</p>
                    </div>

                    <div className="flex space-x-2">
                        {pilot.canComplete && pilot.status !== 'completed' && (
                            <button
                                onClick={handleCompletePilot}
                                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                            >
                                <CheckCircleIcon className="w-5 h-5" />
                                <span>Complete Pilot</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-gray-100 pt-4">
                    <div>
                        <span className="text-gray-500 block text-xs">Business Partner</span>
                        <span className="font-medium">{pilot.business?.name || 'N/A'}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block text-xs">Start Date</span>
                        <span className="font-medium">{pilot.start_date}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block text-xs">Solution</span>
                        <span className="font-medium">{pilot.solution?.name || 'Custom Solution'}</span>
                    </div>
                    <div>
                        <span className="text-gray-500 block text-xs">Progress</span>
                        <span className="font-medium">{pilot.progress || 0}%</span>
                    </div>
                </div>

                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div 
                        className="bg-black h-full transition-all duration-500" 
                        style={{ width: `${pilot.progress || 0}%` }}
                    />
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex space-x-4 border-b border-gray-200">
                {['overview', 'milestones', 'tasks', 'check-ins', 'metrics', 'feedback'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-2 px-1 text-sm font-medium capitalize border-b-2 transition-colors ${
                            activeTab === tab 
                                ? 'border-black text-black' 
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    {pilot.objectives && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Objectives</h3>
                            <p className="text-sm text-gray-600 mt-1">{pilot.objectives}</p>
                        </div>
                    )}
                    {pilot.success_criteria && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Success Criteria</h3>
                            <p className="text-sm text-gray-600 mt-1">{pilot.success_criteria}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Milestones Tab */}
            {activeTab === 'milestones' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Pilot Milestones</h3>
                        <button
                            onClick={() => setShowMilestoneModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Milestone</span>
                        </button>
                    </div>

                    {pilot.milestones?.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No milestones defined yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {pilot.milestones?.map((m) => (
                                <div key={m.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                                    <div>
                                        <h4 className="font-semibold text-sm text-gray-900">{m.name}</h4>
                                        <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>
                                    </div>
                                    <select
                                        value={m.status}
                                        onChange={(e) => handleUpdateMilestone(m.id, e.target.value)}
                                        className="text-xs border border-gray-300 rounded p-1 bg-white"
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="delayed">Delayed</option>
                                    </select>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Tasks</h3>
                        <button
                            onClick={() => setShowTaskModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Task</span>
                        </button>
                    </div>

                    {pilot.tasks?.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No tasks recorded.</p>
                    ) : (
                        <div className="space-y-3">
                            {pilot.tasks?.map((t) => (
                                <div key={t.id} className="border border-gray-200 rounded-lg p-3 flex justify-between items-center text-sm">
                                    <div>
                                        <p className="font-medium text-gray-900">{t.name}</p>
                                        <p className="text-xs text-gray-500 capitalize">{t.priority} priority</p>
                                    </div>
                                    <span className="text-xs px-2 py-0.5 rounded bg-gray-100 font-medium capitalize">
                                        {t.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Check-ins Tab */}
            {activeTab === 'check-ins' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Check-in Logs</h3>
                        <button
                            onClick={() => setShowCheckInModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Check-in</span>
                        </button>
                    </div>

                    {pilot.checkIns?.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No check-ins logged.</p>
                    ) : (
                        <div className="space-y-3">
                            {pilot.checkIns?.map((c) => (
                                <div key={c.id} className="border border-gray-200 rounded-lg p-4 text-sm space-y-1">
                                    <div className="flex justify-between text-xs text-gray-500">
                                        <span>📅 {c.date}</span>
                                        <span className="font-medium capitalize px-2 py-0.5 bg-gray-100 rounded text-gray-800">{c.status.replace('_', ' ')}</span>
                                    </div>
                                    <p className="text-gray-800">{c.progress_summary}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Metrics Tab */}
            {activeTab === 'metrics' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Impact Metrics</h3>
                        <button
                            onClick={() => setShowMetricModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Metric</span>
                        </button>
                    </div>

                    {pilot.metrics?.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No impact metrics recorded.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {pilot.metrics?.map((m) => (
                                <div key={m.id} className="border border-gray-200 rounded-lg p-4 space-y-2">
                                    <div className="flex justify-between items-start">
                                        <h4 className="font-semibold text-sm text-gray-900">{m.metric_name}</h4>
                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium capitalize">
                                            {m.metric_category}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-xs text-center border-t border-gray-100 pt-2">
                                        <div>
                                            <span className="text-gray-400 block">Baseline</span>
                                            <span className="font-medium">{m.baseline_value || '0'} {m.unit}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400 block">Target</span>
                                            <span className="font-medium">{m.target_value || '0'} {m.unit}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400 block">Current</span>
                                            <span className="font-medium text-black">{m.current_value || '0'} {m.unit}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Feedback Tab */}
            {activeTab === 'feedback' && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-900">Stakeholder Feedback</h3>
                        <button
                            onClick={() => setShowFeedbackModal(true)}
                            className="px-3 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center space-x-1"
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>Add Feedback</span>
                        </button>
                    </div>

                    {pilot.feedback?.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No feedback collected yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {pilot.feedback?.map((f) => (
                                <div key={f.id} className="border border-gray-200 rounded-lg p-3 text-sm">
                                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                                        <span className="font-medium capitalize">{f.provider_type}</span>
                                        <span>Rating: {'⭐'.repeat(f.rating || 5)}</span>
                                    </div>
                                    <p className="text-gray-800">{f.comment}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Modals */}
            {showMilestoneModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddMilestone} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Add Milestone</h3>
                        <input
                            type="text"
                            required
                            placeholder="Milestone Name"
                            value={milestoneForm.name}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <textarea
                            placeholder="Description"
                            value={milestoneForm.description}
                            onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            rows={2}
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowMilestoneModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Add</button>
                        </div>
                    </form>
                </div>
            )}

            {showTaskModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddTask} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Add Task</h3>
                        <input
                            type="text"
                            required
                            placeholder="Task Name"
                            value={taskForm.name}
                            onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <select
                            value={taskForm.priority}
                            onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </select>
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowTaskModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Add</button>
                        </div>
                    </form>
                </div>
            )}

            {showCheckInModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddCheckIn} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Record Check-in</h3>
                        <select
                            value={checkInForm.status}
                            onChange={(e) => setCheckInForm({ ...checkInForm, status: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm bg-white"
                        >
                            <option value="on_track">On Track</option>
                            <option value="attention_needed">Attention Needed</option>
                            <option value="at_risk">At Risk</option>
                            <option value="completed">Completed</option>
                        </select>
                        <textarea
                            required
                            placeholder="Progress Summary"
                            value={checkInForm.progress_summary}
                            onChange={(e) => setCheckInForm({ ...checkInForm, progress_summary: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            rows={3}
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowCheckInModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Record</button>
                        </div>
                    </form>
                </div>
            )}

            {showMetricModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddMetric} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Add Impact Metric</h3>
                        <input
                            type="text"
                            required
                            placeholder="Metric Name"
                            value={metricForm.metric_name}
                            onChange={(e) => setMetricForm({ ...metricForm, metric_name: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                        <div className="grid grid-cols-3 gap-2">
                            <input
                                type="number"
                                placeholder="Baseline"
                                value={metricForm.baseline_value}
                                onChange={(e) => setMetricForm({ ...metricForm, baseline_value: e.target.value })}
                                className="p-2 border border-gray-300 rounded text-sm"
                            />
                            <input
                                type="number"
                                placeholder="Target"
                                value={metricForm.target_value}
                                onChange={(e) => setMetricForm({ ...metricForm, target_value: e.target.value })}
                                className="p-2 border border-gray-300 rounded text-sm"
                            />
                            <input
                                type="number"
                                placeholder="Current"
                                value={metricForm.current_value}
                                onChange={(e) => setMetricForm({ ...metricForm, current_value: e.target.value })}
                                className="p-2 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowMetricModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Add</button>
                        </div>
                    </form>
                </div>
            )}

            {showFeedbackModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleAddFeedback} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
                        <h3 className="text-lg font-bold text-gray-900">Record Feedback</h3>
                        <textarea
                            required
                            placeholder="Comment..."
                            value={feedbackForm.comment}
                            onChange={(e) => setFeedbackForm({ ...feedbackForm, comment: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            rows={3}
                        />
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setShowFeedbackModal(false)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-3 py-1.5 bg-black text-white text-xs rounded hover:bg-gray-800">Submit</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default PilotDetail;
